import React, { lazy } from 'react';

import type { Phase1PageId } from '@/lib/phase1Paths';
import type { LaunchToolId } from './booking/LaunchTool';
import type { CrmSurface } from './me/CrmDoor';

/**
 * AR-445 — one component per Phase-1 page, keyed by the id the router walks.
 *
 * Every page is its own chunk: the screens carry sample imagery and catalog
 * fixtures that a member who never opens them should not download, and the
 * whole set is behind a flag that is off for every member today.
 */

type Page = React.LazyExoticComponent<React.ComponentType>;

const launch = (tool: LaunchToolId): Page =>
  lazy(() =>
    import('./booking/LaunchTool').then((m) => ({ default: () => <m.default tool={tool} /> })),
  );

const crm = (surface: CrmSurface): Page =>
  lazy(() => import('./me/CrmDoor').then((m) => ({ default: () => <m.default surface={surface} /> })));

export const PHASE1_PAGE_COMPONENTS: Record<Phase1PageId, Page> = {
  marketing: lazy(() => import('./marketing/MarketingHome')),
  'marketing-web-profiles': lazy(() => import('./marketing/WebProfiles')),
  'marketing-website': lazy(() => import('./marketing/Website')),
  'marketing-email': lazy(() => import('./marketing/EmailMarketing')),
  'marketing-direct-mail': lazy(() => import('./marketing/DirectMail')),
  'marketing-social': lazy(() => import('./marketing/SocialMedia')),
  'marketing-promo-products': lazy(() => import('./marketing/PromoProducts')),
  'marketing-email-newsletters': lazy(() => import('./marketing/EmailNewsletters')),
  'marketing-dynamic-promotions': lazy(() => import('./marketing/DynamicPromotions')),
  'marketing-design-studio': lazy(() => import('./marketing/DesignStudio')),
  'marketing-playbooks': lazy(() => import('./marketing/Playbooks')),

  crm: crm('crm'),
  'crm-pipeline': crm('pipeline'),
  'crm-clients': crm('clients'),
  'crm-trips': crm('trips'),

  booking: lazy(() => import('./booking/BookingHome')),
  'booking-hq-cruise-space': lazy(() => import('./booking/HqCruiseSpace')),
  'booking-group-sailings': lazy(() => import('./booking/GroupSailings')),
  'booking-rate-programs': lazy(() => import('./booking/RatePrograms')),
  'booking-about-trio': lazy(() => import('./booking/AboutTrio')),
  'booking-about-travelport': lazy(() => import('./booking/AboutTravelport')),
  'booking-about-vax': lazy(() => import('./booking/AboutVax')),
  'booking-about-taap': lazy(() => import('./booking/AboutTaap')),
  'booking-agent-air': lazy(() => import('./booking/AgentAir')),
  'booking-launch-trio': launch('trio'),
  'booking-launch-travelport': launch('travelport'),
  'booking-launch-vax': launch('vax'),
  'booking-launch-taap': launch('taap'),

  calendar: lazy(() => import('./network/Calendar')),
  academy: lazy(() => import('./me/AcademyDoor')),
  suppliers: lazy(() => import('./network/Suppliers')),
  'suppliers-yours': lazy(() => import('./network/YourSuppliers')),
  'suppliers-promotions': lazy(() => import('./network/SupplierPromotions')),
  'suppliers-fams': lazy(() => import('./network/Fams')),
  news: lazy(() => import('./network/News')),
  'everything-worldvia': lazy(() => import('./network/EverythingWorldVia')),
  'everything-tln-agent-universe': lazy(() => import('./network/TlnAgentUniverse')),

  support: lazy(() => import('./me/Support')),
};
